import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import { useToast } from 'context/ToastContext';
import { useAutomationList } from '../hooks';
import { toastConstant } from 'constants/toastmessage';
import { useWebSocketContext } from 'context/WebSocketConnectionContext';
import { encode } from 'utils/encryptionUtils';
import { initialFilterValues } from 'modules/settings/workflow_automation/components/ListAutomationErrorLog/configs/datatable_configs';
import GlobalTooltip from 'components/GlobalTooltip';
import useIsMobileView from 'hooks/useIsMobileView';

function AutomationList({ caseId }) {
  const { addToast } = useToast();
  const isMobileView = useIsMobileView();
  const websocketContext = useWebSocketContext();
  const { automationList, loading, skipAction, paginatorTemplate, pageSize } = useAutomationList(caseId);

  const handleSkipAction = (rowData) => {
    confirmDialog({
      message: 'Are you sure you want to skip this automation action?',
      header: 'Skip Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        skipAction(rowData.pk, rowData.sk)
          .then(() => {
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, 'Automation action skipped successfully');
          })
          .catch((error) => {
            console.error('Error skipping automation action:', error);
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'Failed to skip automation action');
          });
      },
    });
  };

  const automationBodyTemplate = (rowData) => {
    const isSkipped = rowData.status === 'skipped';

    return (
      <>
        <span className="p-column-title text-break">Automation</span>
        <div className="automation-cell">
          <div className="automation-title" style={isSkipped ? { textDecoration: 'line-through' } : {}}>
            {rowData.automation}
            {rowData.module && <span className="module-badge ml-2">({rowData.module})</span>}
          </div>
          <div className="automation-description" style={isSkipped ? { textDecoration: 'line-through' } : {}}>
            {rowData.description}
          </div>
          {isSkipped && (
            <div className="skipped-info">
              Skipped by {rowData.skipped_by_first_name} {rowData.skipped_by_last_name} on {rowData.skipped_at}
            </div>
          )}
        </div>
      </>
    );
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Status</span>
        <span className={`status-badge status-${rowData.status.toLowerCase()}`}>{rowData.status}</span>
      </>
    );
  };

  const dateBodyTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Date</span>
        <div className="date-cell">
          <div className="date">{rowData.date}</div>
          {rowData.time && <div className="time">{rowData.time}</div>}
        </div>
      </>
    );
  };

  const errorLogsTemplate = (rowData) => {
    const automation_id = rowData?.execute_link?.workflow_sk?.split('#')?.at(-1);
    const filter = encode(
      JSON.stringify({
        ...initialFilterValues,
        case_lead: [rowData?.case_id],
        case_filter: { label: rowData?.case_name, id: rowData?.case_id },
      })
    );
    let showErrorAlertIcon =
      rowData?.status === 'error' || websocketContext?.automationErrorLogData?.body?.automation_rule_pk === rowData?.pk;

    return (
      <>
        <span className="p-column-title text-break">Error Logs</span>
        <span className="d-inline-table">
          {showErrorAlertIcon ? (
            <GlobalTooltip tooltip="Error Log" position="top">
              <Link
                to={`/workflow-automation/error-logs/${automation_id}?filter=${filter}`}
                className="cursor-pointer text-decoration-none F-size14 call-back"
              >
                <i className="icon-Alert pointer d-block" />
              </Link>
            </GlobalTooltip>
          ) : null}
        </span>
      </>
    );
  };

  const actionBodyTemplate = (rowData) => {
    if (rowData.status !== 'scheduled') {
      return null;
    }

    return (
      <>
        <span className="p-column-title text-break">Actions</span>
        <div className="action-cell">
          <Button
            className="p-button p-button-danger rounded-3"
            label="Skip"
            onClick={() => handleSkipAction(rowData)}
            disabled={loading}
            size="small"
          />
        </div>
      </>
    );
  };

  const triggeredDateBodyTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Triggered Date</span>
        <div className="date-cell">
          <div className="date">{rowData.executedDate}</div>
          {rowData.executedTime && <div className="time">{rowData.executedTime}</div>}
        </div>
      </>
    );
  };

  const ruleNameTemplate = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Rule Name</span>
        <Link
          to={`/workflow-automation/edit/${rowData.execute_link?.workflow_sk?.split('#')?.at(-1)}`}
          className="cursor-pointer call-back text-bold text-decoration-none"
        >
          {rowData.workflow_title}
        </Link>
      </>
    );
  };

  const columns = [
    {
      field: 'automation',
      header: 'Automation',
      body: automationBodyTemplate,
    },
    {
      field: 'workflow_title',
      header: 'Rule Name',
      body: ruleNameTemplate,
    },
    {
      field: 'fire_on.label',
      header: 'Triggered by',
    },
    {
      field: 'date',
      header: 'Triggered Date',
      body: triggeredDateBodyTemplate,
      fixed: true,
      resizable: false,
      width: '120px',
    },
    {
      field: 'status',
      header: 'Status',
      body: statusBodyTemplate,
      fixed: true,
      resizable: false,
      width: '120px',
      style: isMobileView ? {} : { textAlign: 'center' },
    },
    {
      field: 'errors',
      header: 'Errors',
      body: errorLogsTemplate,
      fixed: true,
      resizable: false,
      width: '70px',
      style: isMobileView ? {} : { textAlign: 'center' },
    },
    {
      field: 'date',
      header: 'Scheduled Date',
      body: dateBodyTemplate,
      fixed: true,
      resizable: false,
      width: '120px',
    },
    {
      field: 'action',
      header: 'Action',
      body: actionBodyTemplate,
      fixed: true,
      resizable: false,
      width: '100px',
    },
  ];

  const rowGroupHeaderTemplate = (data) => {
    return <div className="month-header-row">{data.month}</div>;
  };

  const rowGroupFooterTemplate = () => null;

  return (
    <div className="automation-list-container">
      <ResizableDataTable
        value={automationList}
        columns={columns}
        storageKey="automation_case_list_table"
        className="automation-table"
        rowGroupMode="subheader"
        groupField="month"
        sortMode="single"
        rowGroupHeaderTemplate={rowGroupHeaderTemplate}
        rowGroupFooterTemplate={rowGroupFooterTemplate}
        emptyMessage={loading ? 'Loading automation actions...' : 'No automation actions found'}
        paginatorTemplate={paginatorTemplate}
        paginator
        rows={pageSize}
      />
    </div>
  );
}

AutomationList.propTypes = {
  caseId: PropTypes.string,
};

export default AutomationList;
